import { useState, useEffect, useRef } from 'react';
import './CommunityHome.css';
import EditableField from '../components/EditableField';
import { taglineEnding } from '../utils/homePageTagline';

const AddTagCard = ({ onAdd }) => {
  const [adding, setAdding] = useState(false);
  const [val, setVal] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { if (adding && inputRef.current) inputRef.current.focus(); }, [adding]);
  const commit = () => {
    if (val.trim()) onAdd(val.trim());
    setVal('');
    setAdding(false);
  };
  if (!adding) {
    return (
      <div
        className="connect-reason-card_communityHome"
        onClick={() => setAdding(true)}
        style={{ cursor: 'pointer', border: '2px dashed rgba(255,255,255,0.35)', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', opacity: 0.75 }}
      >
        <span className="connect-reason-text_communityHome" style={{ fontSize: '0.9rem' }}>+ Add tag</span>
      </div>
    );
  }
  return (
    <div className="connect-reason-card_communityHome" style={{ padding: '0.4rem' }}>
      <input
        ref={inputRef}
        value={val}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setVal(''); setAdding(false); } }}
        onBlur={commit}
        placeholder="e.g. Hike together"
        style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: '2px solid #f59e0b', outline: 'none', color: 'inherit', font: 'inherit', fontSize: '0.9rem', textAlign: 'center' }}
      />
    </div>
  );
};

const DEFAULT_TESTIMONIALS = [
  { text: 'Really impressed with how quickly I was connected with like-minded people.', author: 'Community Member' },
  { text: 'A great way to meet people who share the same interests and are up for doing things together.', author: 'Community Member' },
];

const strip = (pic, children, extraClass = '') => (
  <div
    className={`imagestrip-fullscreen ${extraClass}`}
    style={{ position: 'relative', overflow: 'hidden' }}
  >
    {pic && (
      <>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${pic})`, backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 0 }} />
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.52)', zIndex: 1 }} />
      </>
    )}
    <div style={{ position: 'relative', zIndex: 2 }}>
      {children}
    </div>
  </div>
);

const VETTING_LABELS = {
  requireEmailVerification: { icon: '✉️', label: 'Verified email required' },
  requireEmailDomain: { icon: '🏫', label: 'Specific email domain required' },
  requireVideoIntro: { icon: '🎥', label: 'Video introduction required' },
  requireManualApproval: { icon: '👤', label: 'Manual approval by leader' },
  approvedEmailsOnly: { icon: '📋', label: 'Approved list only' },
};

const CommunityPreview = ({ fields, images, leader, vetting, onFieldChange, onLeaderChange }) => {
  const edit = onFieldChange ? (key, val) => onFieldChange(key, val) : null;
  const pics = (images || []).map(p => p.url).filter(Boolean);
  const s = (i) => pics[i] || null;
  const [carouselIndex, setCarouselIndex] = useState(0);

  useEffect(() => {
    if (pics.length < 2) return;
    const timer = setInterval(() => setCarouselIndex(i => (i + 1) % pics.length), 3000);
    return () => clearInterval(timer);
  }, [pics.length]);
  const tagsArray = fields.tags ? fields.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
  const connectionReasons = fields.connectionReasons
    ? fields.connectionReasons.split(',').map(t => t.trim()).filter(Boolean)
    : ['Connect', 'Meet up', 'Share experiences', 'Make friends'];

  if (!fields.title) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>
        <div>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>💬</div>
          <p style={{ fontSize: '0.95rem', maxWidth: '260px', lineHeight: 1.6 }}>Answer the question above and your community page will appear here instantly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-content-container_communityHome">

      {/* HERO — pics[0] */}
      <section
        className="section_communityHome hero-section_communityHome imagestrip-fullscreen"
        style={{ position: 'relative', overflow: 'hidden',
          backgroundImage: s(0) ? `url(${s(0)})` : 'linear-gradient(135deg, #1a1a2e, #16213e)',
          backgroundSize: 'cover', backgroundPosition: 'center'
        }}
      >
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.48)', zIndex: 1 }} />
        <div className="section-content_communityHome" style={{ position: 'relative', zIndex: 2 }}>
          <h1 className="community-title-block_communityHome">
            {edit ? <EditableField value={fields.title} fieldKey="title" onSave={edit} style={{ color: 'inherit' }} /> : fields.title}
          </h1>
          <div className="community-hero-actions_communityHome">
            <h3 className="community-hero-subtitle_communityHome" style={{ color: 'white', textShadow: '2px 2px 4px rgba(0,0,0,0.8)', fontSize: '1.5rem' }}>
              {edit
                ? <EditableField value={fields.heroTagline || fields.homePageTagline || 'Find, Connect and Match'} fieldKey="heroTagline" onSave={edit} style={{ color: 'inherit' }} />
                : (fields.heroTagline || fields.homePageTagline || 'Find, Connect and Match')}
            </h3>
            <div className="community-hero-join_communityHome">
              <div className="non-member-buttons_communityHome">
                <button className="join-button_communityHome">Join</button>
                <button className="try-now-button_communityHome">Try now</button>
              </div>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.95)', fontSize: '0.95rem', fontWeight: 600, marginTop: '0.75rem', letterSpacing: '0.02em', textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
              Free to join. Free to use. No costs!
            </p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — pics[1] */}
      <section
        className={`section_communityHome steps-section2_communityHome${s(1) ? ' imagestrip-fullscreen' : ''}`}
        style={s(1) ? { background: 'transparent', overflow: 'hidden', position: 'relative' } : {}}
      >
        {s(1) && (
          <>
            <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${s(1)})`, backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 0 }} />
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.52)', zIndex: 1 }} />
          </>
        )}
        <div className="section-content_communityHome" style={s(1) ? { position: 'relative', zIndex: 2 } : {}}>
          <h2 className="section-heading_communityHome" style={s(1) ? { color: 'white' } : {}}>How it works</h2>
          <StepCards light={!!s(1)} />
        </div>
      </section>

      {/* A PEOPLE FINDING APP FOR — pics[2] */}
      {fields.homePageTagline && (
        <section
          className={`section_communityHome edinburgh-description-section_communityHome${s(2) ? ' imagestrip-fullscreen' : ''}`}
          style={s(2) ? { background: 'transparent', overflow: 'hidden', position: 'relative' } : {}}
        >
          {s(2) && (
            <>
              <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${s(2)})`, backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 0 }} />
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.52)', zIndex: 1 }} />
            </>
          )}
          <div className="section-content_communityHome" style={s(2) ? { position: 'relative', zIndex: 2 } : {}}>
            <h3 className="edinburgh-description-text_communityHome" style={s(2) ? { color: 'white' } : {}}>
              A people finding app for {edit
                ? <EditableField value={fields.homePageTagline} fieldKey="homePageTagline" onSave={edit} style={{ color: 'inherit' }} />
                : taglineEnding(fields.homePageTagline)}.
            </h3>
          </div>
        </section>
      )}

      {/* GET INTRODUCED — full-bleed carousel */}
      <section
        className={`section_communityHome intro-section_communityHome${pics.length > 0 ? ' imagestrip-fullscreen' : ''}`}
        style={pics.length > 0 ? { background: 'transparent', overflow: 'hidden' } : {}}
      >
        {pics.map((pic, idx) => (
          <div
            key={idx}
            className={`hero-carousel-image_communityHome ${idx === carouselIndex ? 'active' : ''}`}
            style={{ backgroundImage: `url(${pic})` }}
          />
        ))}
        {pics.length > 0 && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1 }} />
        )}
        <div style={pics.length > 0 ? { position: 'relative', zIndex: 2, textAlign: 'center' } : {}}>
          <h2 className="section-heading_communityHome" style={pics.length > 0 ? { color: 'white' } : {}}>Get introduced</h2>
        </div>
      </section>

      {/* FIND OTHER PEOPLE TO */}
      <section className="section_communityHome people-connect-section_communityHome">
        <div className="section-content_communityHome">
          <h2 className="section-heading_communityHome">Find other people to</h2>
          <div className="connect-reasons-grid_communityHome">
            {connectionReasons.map((r, i) => (
              <div key={i} className="connect-reason-card_communityHome" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <span className="connect-reason-text_communityHome" style={{ flex: 1, minWidth: 0 }}>
                  {edit
                    ? <EditableField
                        value={r}
                        fieldKey="connectionReasons"
                        onSave={(_, val) => {
                          const updated = [...connectionReasons];
                          updated[i] = val;
                          edit('connectionReasons', updated.join(','));
                        }}
                        style={{ color: 'inherit' }}
                      />
                    : r}
                </span>
                {edit && (
                  <button
                    onClick={() => edit('connectionReasons', connectionReasons.filter((_, j) => j !== i).join(','))}
                    title="Remove tag"
                    className="cv-tag-remove-btn"
                    style={{ flexShrink: 0 }}
                  >✕</button>
                )}
              </div>
            ))}
            {edit && <AddTagCard onAdd={(val) => edit('connectionReasons', [...connectionReasons, val].join(','))} />}
          </div>
        </div>
      </section>

      {/* DETAILS CARDS */}
      <section className="section_communityHome details-section_communityHome">
        <div className="section-content_communityHome">
          <div className="detail-cards_communityHome">
            {fields.location && <div className="detail-card_communityHome"><div className="detail-header_communityHome"><span className="detail-icon_communityHome">📍</span><strong>Location</strong></div><p>{edit ? <EditableField value={fields.location} fieldKey="location" onSave={edit} /> : fields.location}</p></div>}
            {fields.type && <div className="detail-card_communityHome"><div className="detail-header_communityHome"><span className="detail-icon_communityHome">🔖</span><strong>Type</strong></div><p>{edit ? <EditableField value={fields.type} fieldKey="type" onSave={edit} /> : fields.type}</p></div>}
            {fields.topLevelSummary && <div className="detail-card_communityHome"><div className="detail-header_communityHome"><span className="detail-icon_communityHome">🎯</span><strong>Purpose</strong></div><p>{edit ? <EditableField value={fields.topLevelSummary} fieldKey="topLevelSummary" onSave={edit} multiline /> : fields.topLevelSummary}</p></div>}
            <div className="detail-card_communityHome">
              <div className="detail-header_communityHome"><span className="detail-icon_communityHome">🛂</span><strong>Who can join</strong></div>
              {fields.eligibilityDescription && <p style={{ marginBottom: '0.5rem' }}>{edit ? <EditableField value={fields.eligibilityDescription} fieldKey="eligibilityDescription" onSave={edit} multiline /> : fields.eligibilityDescription}</p>}
              {vetting && Object.keys(VETTING_LABELS).some(k => vetting[k]) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.5rem' }}>
                  {Object.entries(VETTING_LABELS).map(([key, { icon, label }]) =>
                    vetting[key] ? (
                      <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, padding: '0.25rem 0.6rem', fontSize: '0.78rem', color: '#475569', fontWeight: 600 }}>
                        {icon} {key === 'requireEmailDomain' && vetting.emailDomain ? `${vetting.emailDomain} email required` : label}
                      </span>
                    ) : null
                  )}
                </div>
              )}
              {vetting?.customConditions && (
                <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.1rem', fontSize: '0.85rem', color: '#475569' }}>
                  {vetting.customConditions.split('\n').map(c => c.trim()).filter(Boolean).map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              )}
            </div>
            <div className="detail-card_communityHome"><div className="detail-header_communityHome"><span className="detail-icon_communityHome">💸</span><strong>Cost</strong></div><p>Free to join!</p></div>
          </div>
        </div>
      </section>

      {/* OUR COMMUNITY — pics[3] */}
      {fields.mainDescription && (
        <section className="section_communityHome description-section_communityHome" style={{ background: '#fff' }}>
          <div className="section-content_communityHome">
            <h2 className="section-heading_communityHome" style={{ color: '#0f172a' }}>Our Community</h2>
            <div className="community-description_communityHome">
              {edit
                ? <EditableField value={fields.mainDescription} fieldKey="mainDescription" onSave={edit} multiline style={{ color: '#0f172a', display: 'block', width: '100%' }} />
                : fields.mainDescription.split('\n').filter(Boolean).map((p, i) => (
                    <p key={i} style={{ color: '#0f172a' }}>{p}</p>
                  ))}
            </div>
          </div>
        </section>
      )}

      {/* ADD LEADER PROMPT — edit mode, no leader set */}
      {edit && onLeaderChange && (!leader || (!leader.name && !leader.bio && !leader.imageUrl)) && (
        <section style={{ background: '#f8fafc', padding: '2rem 1.5rem', textAlign: 'center' }}>
          <div className="section-content_communityHome">
            <button
              onClick={() => onLeaderChange({ name: '', bio: '', imageUrl: '' }, true)}
              style={{ background: '#1a1a2e', color: '#f59e0b', border: '2px dashed #f59e0b', borderRadius: 10, padding: '0.75rem 1.5rem', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer' }}
            >👤 Add yourself as community leader</button>
            <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginTop: '0.5rem' }}>Shows your name, photo and bio on the community page</p>
          </div>
        </section>
      )}

      {/* COMMUNITY LEADER */}
      {leader && (leader.name || leader.bio || leader.imageUrl) && (
        <section style={{ background: '#f8fafc', padding: '3rem 1.5rem', position: 'relative' }}>
          {edit && onLeaderChange && (
            <button
              onClick={() => onLeaderChange({ name: '', bio: '', imageUrl: '' })}
              title="Remove leader section"
              className="cv-leader-remove-btn"
            >✕ Remove leader</button>
          )}
          <div className="section-content_communityHome">
            <h2 className="section-heading_communityHome" style={{ color: '#0f172a', marginBottom: '2rem' }}>Meet Your Community Leader</h2>
            <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              {leader.imageUrl ? (
                <img src={leader.imageUrl} alt={leader.name} style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '3px solid #e2e8f0' }} />
              ) : (
                <div style={{ width: 100, height: 100, borderRadius: '50%', background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '2.5rem' }}>👤</div>
              )}
              <div style={{ flex: 1, minWidth: 200 }}>
                {leader.name && (
                  <p style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0f172a', margin: '0 0 0.5rem' }}>
                    {edit && onLeaderChange
                      ? <EditableField value={leader.name} fieldKey="name" onSave={(_, v) => onLeaderChange({ ...leader, name: v })} style={{ color: 'inherit' }} />
                      : leader.name}
                  </p>
                )}
                {leader.bio && (
                  <p style={{ color: '#475569', lineHeight: 1.6, margin: 0 }}>
                    {edit && onLeaderChange
                      ? <EditableField value={leader.bio} fieldKey="bio" onSave={(_, v) => onLeaderChange({ ...leader, bio: v })} multiline />
                      : leader.bio}
                  </p>
                )}
                <button disabled style={{ display: 'inline-block', marginTop: '1rem', background: '#1a1a2e', color: 'white', padding: '0.6rem 1.25rem', borderRadius: 8, fontWeight: 600, fontSize: '0.875rem', border: 'none', cursor: 'default', opacity: 0.8 }}>
                  Join the community →
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA — pics[4] */}
      <section
        className={`section_communityHome cta-section_communityHome${s(4) ? ' imagestrip-fullscreen' : ''}`}
        style={s(4) ? { background: 'transparent', position: 'relative', overflow: 'hidden' } : {}}
      >
        {s(4) && (
          <>
            <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${s(4)})`, backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 0 }} />
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1 }} />
          </>
        )}
        <div className="section-content_communityHome" style={s(4) ? { position: 'relative', zIndex: 2 } : {}}>
          <p style={{ color: 'white', fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontWeight: 700, marginBottom: '2rem', lineHeight: 1.3 }}>
            Free to join. Free to use. No costs!
          </p>
          <div className="non-member-buttons_communityHome">
            <button className="join-button_communityHome">Join</button>
            <button className="try-now-button_communityHome">Try now</button>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — pics[5] */}
      <section
        className={`section_communityHome${s(5) ? ' imagestrip-fullscreen' : ''}`}
        style={{
          background: s(5) ? 'transparent' : '#f9f9f9',
          padding: '3rem 1.5rem',
          position: s(5) ? 'relative' : undefined,
          overflow: s(5) ? 'hidden' : undefined
        }}
      >
        {s(5) && (
          <>
            <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${s(5)})`, backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 0 }} />
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1 }} />
          </>
        )}
        <div className="section-content_communityHome" style={s(5) ? { position: 'relative', zIndex: 2 } : {}}>
          <h2 className="section-heading_communityHome" style={s(5) ? { color: 'white' } : {}}>What our members say</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', justifyContent: 'center', marginTop: '1.5rem' }}>
            {DEFAULT_TESTIMONIALS.map((t, i) => (
              <div key={i} style={{ background: s(5) ? 'rgba(255,255,255,0.12)' : 'white', borderRadius: 12, padding: '1.5rem 1.75rem', maxWidth: 380, flex: '1 1 280px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                <span style={{ fontSize: '2rem', lineHeight: 1, color: s(5) ? '#f59e0b' : '#1976d2', display: 'block', marginBottom: '0.75rem' }}>"</span>
                <p style={{ margin: 0, fontStyle: 'italic', color: s(5) ? 'rgba(255,255,255,0.9)' : '#333', lineHeight: 1.6 }}>{t.text}</p>
                <p style={{ margin: '1rem 0 0', fontWeight: 600, fontSize: '0.9rem', color: s(5) ? 'rgba(255,255,255,0.6)' : '#666' }}>— {t.author}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TAGS */}
      {tagsArray.length > 0 && (
        <section className="tags-hashtag-section_communityHome">
          <div className="tags-hashtag-inner_communityHome">
            {tagsArray.map((tag, i) => (
              <span key={i} className="tags-hashtag-item_communityHome">#{tag}</span>
            ))}
          </div>
        </section>
      )}

    </div>
  );
};

const StepCards = ({ light }) => (
  <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
    {['Describe who you want to connect with', 'Get suggested compatible people', 'Match and connect!'].map((step, i) => (
      <div key={i} style={{ flex: '1 1 180px', background: light ? 'rgba(255,255,255,0.15)' : '#f1f5f9', borderRadius: 12, padding: '1rem 1.25rem' }}>
        <div style={{ fontWeight: 800, fontSize: '1.1rem', marginBottom: '0.4rem', color: light ? '#f59e0b' : '#6366f1' }}>{i + 1}</div>
        <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: 1.5, color: light ? 'rgba(255,255,255,0.9)' : '#0f172a' }}>{step}</p>
      </div>
    ))}
  </div>
);

export default CommunityPreview;
